import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import ApiService from "../services/apiService";
import getApiErrorMessage from "../utils/getApiErrorMessage";

export default function ServiceManagement() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getServices();
      if (!Array.isArray(data)) {
        throw new Error("Invalid services response.");
      }
      setServices(data);
    } catch (e) {
      console.error("Error loading services:", e);
      setError(getApiErrorMessage(e, "Failed to load services."));
    } finally {
      setLoading(false);
    }
  };

  const filtered = services.filter(s => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = (
      (s.offers || "").toLowerCase().includes(term) ||
      (s.description || "").toLowerCase().includes(term) ||
      (s.shop_name || "").toLowerCase().includes(term) ||
      String(s.shop_id || "").includes(searchTerm) ||
      String(s.service_id || "").includes(searchTerm)
    );
    const matchesStatus = filterStatus === 'all' || (s.status || '').toLowerCase() === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const formatPrice = (p) => {
    const n = Number(p || 0);
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(isNaN(n) ? 0 : n);
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 50, height: 50, border: '4px solid #e2e8f0', borderTop: '4px solid #8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
            <p style={{ color: '#64748b' }}>Loading services...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <style>{`
        @media (max-width: 767px) {
          .service-table-wrapper {
            display: none !important;
          }
          .service-cards-wrapper {
            display: grid !important;
            gap: 12px;
          }
          .service-filters {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .service-filters input,
          .service-filters select {
            width: 100% !important;
            min-width: 0 !important;
          }
        }

        @media (min-width: 768px) {
          .service-cards-wrapper {
            display: none !important;
          }
        }
      `}</style>

      <div>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: '#fff', padding: 24, borderRadius: 12, marginBottom: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Service Catalog</h1>
          <p style={{ margin: '6px 0 0 0', opacity: 0.9 }}>View all services across shops</p>
        </div>

        {error && <div style={{ marginBottom: 16, padding: 12, borderLeft: '4px solid #ef4444', background: '#fef2f2', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span>{error}</span>
          <button type="button" onClick={loadServices} disabled={loading} style={{
            marginLeft: 'auto',
            border: '1px solid #fca5a5',
            borderRadius: '6px',
            background: '#fff',
            color: '#b91c1c',
            padding: '6px 10px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600
          }}>
            Retry
          </button>
        </div>}

        {/* Filters */}
        <div className="service-filters" style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by shop, offer, description..."
            style={{ flex: 1, minWidth: 260, padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8 }}
            onFocus={(e)=> e.target.style.borderColor = '#8b5cf6'}
            onBlur={(e)=> e.target.style.borderColor = '#e2e8f0'}
          />
          <select value={filterStatus} onChange={(e)=> setFilterStatus(e.target.value)} style={{ padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: 8, minWidth: 150 }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="service-table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', color: '#fff' }}>
                    <th style={{ padding: 12, textAlign: 'left' }}>Shop</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Offer</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Description</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Quantity</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Price</th>
                    <th style={{ padding: 12, textAlign: 'left' }}>Package</th>
                    <th style={{ padding: 12, textAlign: 'left', borderTopRightRadius: 12 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => (
                    <tr key={s.service_id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: 12 }}>{s.shop_name || (s.shop_id ? `Shop #${s.shop_id}` : '-')}</td>
                      <td style={{ padding: 12 }}>{s.offers || '-'}</td>
                      <td style={{ padding: 12, maxWidth: 400 }}>{s.description || '-'}</td>
                      <td style={{ padding: 12 }}>{s.quantity ?? '-'}</td>
                      <td style={{ padding: 12 }}>{formatPrice(s.price)}</td>
                      <td style={{ padding: 12 }}>{s.package || '-'}</td>
                      <td style={{ padding: 12 }}>
                        <span style={{ padding: '4px 10px', borderRadius: 16, background: (s.status||'').toLowerCase()==='active' ? '#dcfce7' : '#fee2e2', color: (s.status||'').toLowerCase()==='active' ? '#166534' : '#991b1b', fontSize: 12, fontWeight: 600 }}>
                          {s.status || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="service-cards-wrapper" style={{ display: 'none' }}>
              {filtered.map((s) => (
                <div key={s.service_id} style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                    <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Service</div>
                    <span style={{ padding: '4px 10px', borderRadius: 16, background: (s.status||'').toLowerCase()==='active' ? '#dcfce7' : '#fee2e2', color: (s.status||'').toLowerCase()==='active' ? '#166534' : '#991b1b', fontSize: 11, fontWeight: 700 }}>
                      {s.status || 'Unknown'}
                    </span>
                  </div>

                  <div style={{ display: 'grid', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Shop</div>
                      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{s.shop_name || (s.shop_id ? `Shop #${s.shop_id}` : '-')}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Offer</div>
                      <div style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{s.offers || '-'}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Description</div>
                      <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.5 }}>{s.description || '-'}</div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Quantity</div>
                        <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{s.quantity ?? '-'}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Price</div>
                        <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{formatPrice(s.price)}</div>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, marginBottom: 4 }}>Package</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{s.package || '-'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, padding: 40, textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>🧺</div>
            <div style={{ color: '#1e293b', fontWeight: 600, marginBottom: 6 }}>No services found</div>
            <div style={{ color: '#64748b' }}>Try clearing filters or add services from a shop.</div>
          </div>
        )}
      </div>
    </Layout>
  );
}
